import { useEffect, useRef } from 'react';

/**
 * A FALHA PASSAGEIRA — a rede que caiu ou o sistema que esta reiniciando.
 *
 * Os robos no QAS (20/10/2026, item 3) pegaram a implantacao no meio: durante
 * uns 15 segundos o proxy respondeu sem a API atras dele, a resposta veio sem
 * `Access-Control-Allow-Origin` (quem a deu foi o proxy, nao a API), e o
 * navegador a entregou a tela como falha de rede. A tela disse "O sistema nao
 * respondeu", uma vez por consulta, e parou ali — com a API de pe de novo
 * segundos depois.
 *
 * A API, essa, ja leva o CORS em toda resposta de erro dela
 * (`backend/testes/cors-em-toda-resposta.test.ts`). O que falta e a tela
 * reconhecer o que e passageiro e TENTAR DE NOVO SOZINHA, com espera
 * crescente, antes de avisar; e, quando avisar, dizer uma frase so (o toast
 * igual nao empilha) que nomeia as duas causas e o que acontece em seguida.
 *
 * Funciona para as tres familias de erro de pedido que existem no app: a
 * `ApiIndisponivel` (lib/pedido e as copias dos modulos), o `ErroDaApi` com
 * `status` 0 (equipamentos e cadastros) e o `TypeError` cru do `fetch`. O
 * 502/503/504 tambem e passageiro: e o que o proxy da na mesma origem.
 */

/** Quantas vezes a leitura tenta de novo sozinha antes de avisar. */
export const TENTATIVAS_NA_FALHA_DE_REDE = 5;

/** A espera antes da tentativa `n` (0, 1, 2…): 1 s, 2 s, 4 s, 8 s, 8 s — uns 23 s ao todo. */
export function esperaDaTentativa(tentativa: number): number {
  return Math.min(1000 * 2 ** tentativa, 8000);
}

/** A falha que passa sozinha: sem resposta da API, ou o proxy sem a API atras. */
export function ehFalhaPassageira(erro: unknown): boolean {
  if (erro === null || typeof erro !== 'object') return false;
  const e = erro as { name?: unknown; status?: unknown; message?: unknown };
  if (e.name === 'ApiIndisponivel') return true;
  if (e.status === 0) return true;
  if (e.status === 502 || e.status === 503 || e.status === 504) return true;
  /* O `fetch` cru rejeita com TypeError: "Failed to fetch" (Chromium), "Load
     failed" (WebKit), "NetworkError…" (Firefox). */
  return erro instanceof TypeError && typeof e.message === 'string' && /fetch|load failed|network/i.test(e.message);
}

/**
 * A regra do `retry` das leituras: a falha passageira tenta de novo ate o teto;
 * a recusa do servidor (4xx) nao — ela nao muda sozinha, e repetir um 403 e so
 * pedir de novo o que ja foi negado —; o resto, uma vez, como era.
 */
export function deveTentarDeNovo(tentativas: number, erro: unknown): boolean {
  if (ehFalhaPassageira(erro)) return tentativas < TENTATIVAS_NA_FALHA_DE_REDE;
  const status = (erro as { status?: unknown } | null)?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return tentativas < 1;
}

/** A frase unica da falha passageira (R04: o que houve e o que fazer). */
export const FALHA_DE_REDE = {
  titulo: 'O sistema não respondeu.',
  comoResolver:
    'Pode ser a sua conexão ou o sistema reiniciando por uma atualização. A tela tenta de novo sozinha; se passar de alguns minutos, avise o suporte com o horário.',
} as const;

/** De quanto em quanto tempo a tela parada na falha tenta de novo. */
export const INTERVALO_DA_NOVA_TENTATIVA_MS = 10_000;

/**
 * A tela parada na falha (a casca sem resposta da sessao) tenta de novo
 * sozinha: a cada 10 s e assim que o aparelho diz que a rede voltou. O botao
 * "Tentar de novo" continua para quem nao quer esperar.
 */
export function useTentarDeNovoSozinho(emFalha: boolean, tentar: () => void): void {
  /* A funcao fica num ref: a casca a escreve em linha, e um efeito que
     dependesse dela reiniciaria o relogio a cada desenho — e nunca tentaria. */
  const atual = useRef(tentar);
  useEffect(() => {
    atual.current = tentar;
  });
  useEffect(() => {
    if (!emFalha) return undefined;
    const disparar = () => atual.current();
    const alca = window.setInterval(disparar, INTERVALO_DA_NOVA_TENTATIVA_MS);
    window.addEventListener('online', disparar);
    return () => {
      window.clearInterval(alca);
      window.removeEventListener('online', disparar);
    };
  }, [emFalha]);
}
